/**
 * sentry.js — Initialisation de la remontée d'erreurs frontend (Sentry)
 *
 * No-op si REACT_APP_SENTRY_DSN n'est pas défini au build (dev local, tests).
 * Le DSN navigateur est public par conception (il finit dans le bundle JS) et
 * versionné dans frontend/.env.production (D60 complétée, S5).
 *
 * Aucune donnée personnelle volontaire : sendDefaultPii false, pas de Replay,
 * pas de traces. Les URL (page, requêtes) et les messages peuvent pourtant
 * contenir un email ou un jeton (lien de réinitialisation `?token=`, partage
 * `?share=`, `/api/shares/<jeton>/accept`) : beforeSend et beforeBreadcrumb
 * les masquent avant tout envoi.
 *
 * Aucune valeur de query n'est envoyée, seuls les noms des paramètres restent
 * (D213) : une liste noire de paramètres avait oublié `share`. Même principe
 * que backend/utils/urlRedaction.js (le bundle CRA ne peut pas l'importer).
 *
 * Lien de séance (D247) : le jeton est un fragment nu (`/seance-partagee#<jeton>`),
 * sans `=` : tout fragment est retiré sur ce chemin, et tout fragment nu d'au
 * moins 32 caractères ailleurs.
 */
import * as Sentry from '@sentry/react';

const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
// Couple `nom=valeur` après `?`, `&` ou `#` : seule la valeur disparaît
const QUERY_PAIR_RE = /([?&#])([^=&#?\s"'<>]+)=[^&#\s"'<>]*/g;
// Segment de chemin d'au moins 32 caractères : jeton (64 hex), pas un ObjectId (24)
const SECRET_SEGMENT_RE = /\/[A-Za-z0-9_-]{32,}(?=[/?#\s"'&]|$)/g;
// Fragment de la page du lien de séance, quel qu'il soit
const SESSION_LINK_FRAGMENT_RE = /(\/seance-partagee[^#\s"'<>]*)#[^\s"'<>]*/g;
// Fragment nu d'au moins 32 caractères (jeton), ailleurs
const SECRET_FRAGMENT_RE = /#[A-Za-z0-9_-]{32,}(?=[\s"'<>]|$)/g;
// Clés d'événement portant une query string brute, sans `?`
const RAW_QUERY_KEYS = new Set(['query_string', 'http.query', 'http.fragment']);
const FILTERED = '[Filtered]';

/** Noms des paramètres d'une query string brute (`a=1&b=2` → `a&b`). */
function queryNames(raw, key) {
  // Fragment sans `nom=valeur` : c'est une valeur seule (jeton possible)
  if (key === 'http.fragment' && typeof raw === 'string' && raw && !raw.includes('=')) return FILTERED;
  if (typeof raw !== 'string') return raw && typeof raw === 'object' ? Object.keys(raw).join('&') : raw;
  return raw.replace(/^[?#]/, '').split('&').filter(Boolean).map((pair) => pair.split('=')[0]).join('&');
}

/** Retire valeurs de query, segments-jetons et emails d'une chaîne. */
export function scrubString(value) {
  if (typeof value !== 'string') return value;
  return value
    .replace(SESSION_LINK_FRAGMENT_RE, '$1')
    .replace(SECRET_FRAGMENT_RE, '')
    .replace(QUERY_PAIR_RE, '$1$2')
    .replace(SECRET_SEGMENT_RE, '/:token')
    .replace(EMAIL_RE, FILTERED);
}

/** Copie profonde où chaque chaîne est masquée (objets et tableaux simples). */
function scrubDeep(value, depth = 0) {
  if (typeof value === 'string') return scrubString(value);
  if (value === null || typeof value !== 'object') return value;
  // Au-delà, rien n'est renvoyé brut : une chaîne non masquée pourrait fuir
  if (depth > 10) return FILTERED;
  if (Array.isArray(value)) return value.map((v) => scrubDeep(v, depth + 1));
  const out = {};
  for (const [k, v] of Object.entries(value)) {
    out[k] = RAW_QUERY_KEYS.has(k) ? queryNames(v, k) : scrubDeep(v, depth + 1);
  }
  return out;
}

export function scrubEvent(event) {
  const clean = scrubDeep(event);
  // Aucune identité utilisateur : ni email, ni IP, ni identifiant
  delete clean.user;
  return clean;
}

// Sentry passe (breadcrumb, hint) : le hint ne doit pas devenir la profondeur
export const scrubBreadcrumb = (breadcrumb) => scrubDeep(breadcrumb);

/**
 * @param {Object} env process.env (injectable pour les tests)
 * @returns {boolean} true si Sentry a été initialisé
 */
export function initSentry(env = process.env) {
  if (!env.REACT_APP_SENTRY_DSN) return false;
  const buildHash = env.REACT_APP_BUILD_HASH;
  Sentry.init({
    dsn:              env.REACT_APP_SENTRY_DSN,
    environment:      env.NODE_ENV || 'development',
    // Hash git du build (injecté par scripts/inject-build-info.js au prebuild) :
    // relie une erreur à la version déployée, comme CACHE_VERSION.
    release:          buildHash && buildHash !== 'dev' ? `ekynair-web@${buildHash}` : undefined,
    tracesSampleRate: 0,
    sendDefaultPii:   false,
    beforeSend:       scrubEvent,
    beforeBreadcrumb: scrubBreadcrumb,
  });
  return true;
}
